import { API_BASE_URL } from '../config/api';
import {
    Account,
    Message,
    User,
    Contact,
    SuccessResponse,
    NewAccountCreated,
    LoginResponse,
    AccountContactsMetadata,
    AccountSubscription,
    AdminCreateAccountSubscriptionRequest,
    AdminUpdateAccountSubscriptionRequest,
    AccountSettings,
    UserUpdate,
    AdminUpdateUserRequest,
    AccountContacts,
} from '../types/generated';
import {
    DashboardStats,
    PaginatedResponse,
    PendingUser,
    RecentMessage,
} from '../types/api';
import {
    MessageRequestInput,
    LoginRequestInput,
    RegisterRequestInput,
    UpdatePasswordInput,
    messageRequestSchema,
    loginRequestSchema,
    registerRequestSchema,
    updatePasswordSchema,
} from '../schemas/api';
import {
    ApiError,
    AuthenticationError,
    NetworkError,
    NotFoundError,
    ServerError,
} from './errors';

type RequestOptions = RequestInit & {
    params?: Record<string, string | number | boolean>;
};

export class ApiService {
    private token: string | null = null;

    constructor() {
        this.token = localStorage.getItem('token');
    }

    private getAuthHeaders(): HeadersInit {
        if (!this.token) {
            throw new AuthenticationError();
        }
        return {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json',
        };
    }

    private async handleResponse<T>(response: Response): Promise<T> {
        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
            } catch {
                // If JSON parsing fails, use default error handling
                if (response.status === 404) {
                    throw new NotFoundError();
                }
                if (response.status === 500) {
                    throw new ServerError();
                }
                throw ApiError.fromResponse(response);
            }

            // Handle specific error cases
            switch (response.status) {
                case 401:
                    throw new AuthenticationError(errorData.message);
                case 404:
                    throw new NotFoundError(errorData.message);
                case 500:
                    throw new ServerError(errorData.message);
                default:
                    throw ApiError.fromResponse(response, errorData);
            }
        }

        return response.json();
    }

    private async fetch<T>(
        endpoint: string,
        options: RequestOptions = {}
    ): Promise<T> {
        const { params, ...requestOptions } = options;

        // Build URL with query parameters
        const url = new URL(`${API_BASE_URL}${endpoint}`);
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                url.searchParams.append(key, String(value));
            });
        }

        try {
            const response = await fetch(url.toString(), {
                ...requestOptions,
                headers: {
                    ...this.getAuthHeaders(),
                    ...requestOptions.headers,
                },
            });

            return this.handleResponse<T>(response);
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new NetworkError();
        }
    }

    // Account endpoints
    async getAccounts(): Promise<SuccessResponse<Account[]>> {
        return this.fetch<SuccessResponse<Account[]>>('/account/');
    }

    async createAccount(): Promise<SuccessResponse<NewAccountCreated>> {
        return this.fetch<SuccessResponse<NewAccountCreated>>('/account/', {
            method: 'POST',
        });
    }

    async syncAccount(id: string): Promise<SuccessResponse<void>> {
        return this.fetch<SuccessResponse<void>>(`/account/${id}/sync`, {
            method: 'POST',
        });
    }

    async loginAccount(id: string): Promise<SuccessResponse<NewAccountCreated>> {
        return this.fetch<SuccessResponse<NewAccountCreated>>(`/account/${id}/login`, {
            method: 'POST',
        });
    }

    async logoutAccount(id: string): Promise<SuccessResponse<void>> {
        return this.fetch<SuccessResponse<void>>(`/account/${id}/logout`, {
            method: 'POST',
        });
    }

    // Message endpoints
    async getMessages(page: number = 1, limit: number = 10): Promise<SuccessResponse<Message[]>> {
        return this.fetch<SuccessResponse<Message[]>>('/message/', {
            params: { page, limit },
        });
    }

    async createMessage(message: MessageRequestInput): Promise<SuccessResponse<Message>> {
        const validated = messageRequestSchema.parse(message);
        return this.fetch<SuccessResponse<Message>>('/message/', {
            method: 'POST',
            body: JSON.stringify(validated),
        });
    }

    async sendMessage(messageId: number): Promise<SuccessResponse<Message>> {
        return this.fetch<SuccessResponse<Message>>(`/message/${messageId}/send`, {
            method: 'POST',
        });
    }

    async deleteMessage(messageId: number): Promise<SuccessResponse<void>> {
        return this.fetch<SuccessResponse<void>>(`/message/${messageId}`, {
            method: 'DELETE',
        });
    }

    // Contact endpoints
    async getContacts(accountId: string): Promise<SuccessResponse<AccountContacts>> {
        return this.fetch<SuccessResponse<AccountContacts>>(`/account/${accountId}/contacts`);
    }

    async getAccountContactsMetadata(id: string): Promise<SuccessResponse<AccountContactsMetadata>> {
        return this.fetch<SuccessResponse<AccountContactsMetadata>>(`/account/${id}/contacts/meta`);
    }

    // Auth endpoints
    async register(data: RegisterRequestInput): Promise<SuccessResponse<void>> {
        try {
            const validated = registerRequestSchema.parse(data);
            const response = await fetch(`${API_BASE_URL}/auth/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(validated),
            });

            return this.handleResponse<SuccessResponse<void>>(response);
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new NetworkError('Registration failed');
        }
    }

    async login(credentials: LoginRequestInput): Promise<SuccessResponse<LoginResponse>> {
        try {
            const validated = loginRequestSchema.parse(credentials);
            const response = await fetch(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(validated),
            });

            const data = await this.handleResponse<SuccessResponse<LoginResponse>>(response);
            this.token = data.data.token;
            localStorage.setItem('token', this.token);
            return data;
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new NetworkError('Login failed');
        }
    }

    async logout(): Promise<void> {
        localStorage.removeItem('token');
        this.token = null;
    }

    // User endpoints
    async getCurrentUser(): Promise<SuccessResponse<User>> {
        return this.fetch<SuccessResponse<User>>('/user/me');
    }

    async updateCurrentUser(data: Partial<UserUpdate>): Promise<SuccessResponse<User>> {
        return this.fetch<SuccessResponse<User>>('/user/me', {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async updatePassword(data: UpdatePasswordInput): Promise<SuccessResponse<void>> {
        const validated = updatePasswordSchema.parse(data);
        return this.fetch<SuccessResponse<void>>('/user/password', {
            method: 'PUT',
            body: JSON.stringify(validated),
        });
    }

    // Admin endpoints
    async getUsers(): Promise<SuccessResponse<User[]>> {
        return this.fetch<SuccessResponse<User[]>>('/admin/users');
    }

    async updateUser(userId: string, data: Partial<AdminUpdateUserRequest>): Promise<SuccessResponse<User>> {
        return this.fetch<SuccessResponse<User>>(`/admin/users/${userId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async updateAccountSettings(accountId: string, settings: AccountSettings): Promise<SuccessResponse<Account>> {
        return this.fetch<SuccessResponse<Account>>(`/account/${accountId}/settings`, {
            method: 'PATCH',
            body: JSON.stringify(settings),
        });
    }

    async getUserAccounts(userId: string): Promise<SuccessResponse<Account[]>> {
        return this.fetch<SuccessResponse<Account[]>>(`/admin/users/${userId}/accounts`);
    }

    async getUserAccount(userId: string, accountId: string): Promise<SuccessResponse<Account>> {
        return this.fetch<SuccessResponse<Account>>(`/admin/users/${userId}/accounts/${accountId}`);
    }

    async getAccountSubscriptions(userId: string, accountId: string): Promise<SuccessResponse<AccountSubscription[]>> {
        return this.fetch<SuccessResponse<AccountSubscription[]>>(
            `/admin/users/${userId}/accounts/${accountId}/subscriptions`
        );
    }

    async createAccountSubscription(
        userId: string,
        accountId: string,
        data: AdminCreateAccountSubscriptionRequest
    ): Promise<SuccessResponse<AccountSubscription>> {
        return this.fetch<SuccessResponse<AccountSubscription>>(
            `/admin/users/${userId}/accounts/${accountId}/subscriptions`,
            {
                method: 'POST',
                body: JSON.stringify(data),
            }
        );
    }

    async updateAccountSubscription(
        userId: string,
        accountId: string,
        subscriptionId: string,
        data: AdminUpdateAccountSubscriptionRequest
    ): Promise<SuccessResponse<AccountSubscription>> {
        return this.fetch<SuccessResponse<AccountSubscription>>(
            `/admin/users/${userId}/accounts/${accountId}/subscriptions/${subscriptionId}`,
            {
                method: 'PATCH',
                body: JSON.stringify(data),
            }
        );
    }

    // Admin Dashboard endpoints
    async getDashboardStats(): Promise<SuccessResponse<DashboardStats>> {
        return this.fetch<SuccessResponse<DashboardStats>>('/admin/dashboard/stats');
    }

    async getPendingUsers(): Promise<SuccessResponse<PendingUser[]>> {
        return this.fetch<SuccessResponse<PendingUser[]>>('/admin/dashboard/pending-users');
    }

    async getRecentMessages(page: number = 1, limit: number = 10, from?: string): Promise<PaginatedResponse<RecentMessage[]>> {
        return this.fetch<PaginatedResponse<RecentMessage[]>>('/admin/dashboard/recent-messages', {
            params: {
                page,
                limit,
                ...(from && { from }),
            },
        });
    }

    async generateUploadUrl(filename: string): Promise<SuccessResponse<{ url: string; key: string }>> {
        return this.fetch<SuccessResponse<{ url: string, key: string }>>(`/media/upload/url?filename=${filename}`);
    }

    async createMediaFromUrl(key: string, filename: string, mime_type: string): Promise<SuccessResponse<{ id: string }>> {
        return this.fetch<SuccessResponse<{ id: string }>>('/media/upload/url', {
            method: 'POST',
            body: JSON.stringify({ url: key, filename, mime_type }),
        });
    }

    async uploadMedia(file: File): Promise<SuccessResponse<{ id: string }>> {
        const formData = new FormData();
        formData.append('file', file);

        const url = new URL(`${API_BASE_URL}/media/`);
        try {
            const response = await fetch(url.toString(), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.token}`,
                },
                body: formData,
            });

            return this.handleResponse<SuccessResponse<{ id: string }>>(response);
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new NetworkError('Failed to upload media');
        }
    }
}

export const apiService = new ApiService();
